import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchSchedules, 
  createSchedule, 
  updateSchedule, 
  deleteSchedule,
  activateSchedule,
  deactivateSchedule
} from '../redux/thunks/schedulesThunks';
import { fetchAutomations } from '../redux/thunks/automationsThunks';
import { 
  Box, 
  Button, 
  Container, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid, 
  IconButton, 
  InputLabel, 
  MenuItem, 
  Paper, 
  Select, 
  Switch,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TextField, 
  Typography 
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import cronstrue from 'cronstrue';

const ScheduleManager = () => {
  const dispatch = useDispatch();
  const { schedules, loading, error } = useSelector(state => state.schedules);
  const { automations } = useSelector(state => state.automations);
  
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentSchedule, setCurrentSchedule] = useState({
    name: '',
    description: '',
    cronPattern: '0 9 * * 1-5', // Default: Weekdays at 9 AM
    timezone: 'UTC',
    isActive: true,
    automationId: ''
  });
  
  useEffect(() => {
    dispatch(fetchSchedules());
    dispatch(fetchAutomations());
  }, [dispatch]);
  
  const handleOpen = () => {
    setOpen(true);
  };
  
  const handleClose = () => {
    setOpen(false);
    setIsEditing(false);
    setCurrentSchedule({
      name: '',
      description: '',
      cronPattern: '0 9 * * 1-5',
      timezone: 'UTC',
      isActive: true,
      automationId: ''
    });
  };
  
  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setCurrentSchedule({
      ...currentSchedule,
      [name]: name === 'isActive' ? checked : value
    });
  };
  
  const handleSubmit = () => {
    if (isEditing) {
      dispatch(updateSchedule({ id: currentSchedule.id, schedule: currentSchedule }));
    } else {
      dispatch(createSchedule(currentSchedule));
    }
    handleClose();
  };
  
  const handleEdit = (schedule) => {
    setCurrentSchedule(schedule);
    setIsEditing(true);
    setOpen(true);
  };
  
  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this schedule?')) {
      dispatch(deleteSchedule(id));
    }
  };
  
  const handleToggleActive = (id, isActive) => {
    if (isActive) {
      dispatch(deactivateSchedule(id));
    } else {
      dispatch(activateSchedule(id));
    }
  };
  
  const formatNextRun = (nextScheduledAt) => {
    if (!nextScheduledAt) return 'Not scheduled';
    return new Date(nextScheduledAt).toLocaleString();
  };
  
  const formatCronPattern = (pattern) => {
    try {
      return cronstrue.toString(pattern);
    } catch (error) {
      return pattern;
    }
  };
  
  const getAutomationName = (automationId) => {
    const automation = automations.find(a => a.id === automationId);
    return automation ? automation.name : 'Unknown';
  };
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Grid item>
            <Typography variant="h4" component="h1" gutterBottom>
              Schedule Manager
            </Typography>
          </Grid>
          <Grid item>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<Add />} 
              onClick={handleOpen}
            >
              Create Schedule
            </Button>
          </Grid>
        </Grid>
        
        {error && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'error.light', color: 'error.contrastText' }}>
            <Typography>{error}</Typography>
          </Paper>
        )}
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Automation</TableCell>
                <TableCell>Schedule</TableCell>
                <TableCell>Next Run</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">Loading...</TableCell>
                </TableRow>
              ) : schedules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">No schedules found</TableCell>
                </TableRow>
              ) : (
                schedules.map(schedule => (
                  <TableRow key={schedule.id}>
                    <TableCell>
                      <Typography variant="body1">{schedule.name}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {schedule.description}
                      </Typography>
                    </TableCell>
                    <TableCell>{getAutomationName(schedule.automationId)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatCronPattern(schedule.cronPattern)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {schedule.cronPattern} ({schedule.timezone})
                      </Typography>
                    </TableCell>
                    <TableCell>{formatNextRun(schedule.nextScheduledAt)}</TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={schedule.isActive}
                            onChange={() => handleToggleActive(schedule.id, schedule.isActive)}
                            color="primary"
                          />
                        }
                        label={schedule.isActive ? 'Active' : 'Inactive'}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        color="primary" 
                        onClick={() => handleEdit(schedule)}
                        size="small"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton 
                        color="error" 
                        onClick={() => handleDelete(schedule.id)}
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Schedule' : 'Create Schedule'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="name"
                label="Schedule Name"
                value={currentSchedule.name}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Automation</InputLabel>
                <Select
                  name="automationId"
                  value={currentSchedule.automationId}
                  onChange={handleChange}
                  label="Automation"
                >
                  {automations.map(automation => (
                    <MenuItem key={automation.id} value={automation.id}>
                      {automation.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                value={currentSchedule.description || ''}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="cronPattern"
                label="CRON Pattern"
                value={currentSchedule.cronPattern}
                onChange={handleChange}
                fullWidth
                required
                helperText={
                  currentSchedule.cronPattern 
                    ? `Runs ${formatCronPattern(currentSchedule.cronPattern)}`
                    : 'Enter a valid CRON pattern'
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="timezone"
                label="Timezone"
                value={currentSchedule.timezone}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    name="isActive"
                    checked={currentSchedule.isActive}
                    onChange={handleChange}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={!currentSchedule.name || !currentSchedule.cronPattern || !currentSchedule.automationId}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ScheduleManager;
